import React, { useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import {
  FiArrowLeft,
  FiArrowRight,
  FiCheck,
  FiCheckCircle,
  FiMapPin,
  FiPackage,
  FiTruck,
  FiExternalLink,
  FiLink,
} from "react-icons/fi";
import AccountNav from "./AccountNav";
import {
  customers,
  formatAccountDate,
  formatOrderAmount,
  orders,
} from "../Datas/accountData";
import siteConfig from "../config/siteConfig";
import "../css/account.scss";

function OrderDetail() {
  const { orderId } = useParams();
  const order = orders.find((item) => item.id === orderId);
  const seller = customers.find((item) => item.id === order?.sellerId);

  useEffect(() => {
    document.title = order
      ? `${order.id} — ${siteConfig.brandName}`
      : `Order not found — ${siteConfig.brandName}`;
  }, [order]);

  if (!order) {
    return (
      <main className="account-page">
        <div className="shell account-empty">
          <p className="eyebrow">Order lookup</p>
          <h1>Order not found</h1>
          <p>The order number does not match an order in this account.</p>
          <Link className="btn" to="/orders">
            Return to orders
          </Link>
        </div>
      </main>
    );
  }

  return (
    <main className="account-page">
      <div className="shell">
        <AccountNav />
        <Link className="account-back" to="/orders">
          <FiArrowLeft /> All orders
        </Link>
        <header className="order-detail-heading">
          <div>
            <p className="eyebrow">Order details</p>
            <h1>{order.id}</h1>
            <p className="order-detail-code">{order.code}</p>
            <p>
              Placed for {order.customer} on {formatAccountDate(order.issuedAt)}
            </p>
          </div>
          <span className="status-badge status-badge--success status-badge--large">
            <FiCheckCircle /> {order.carrierStatus}
          </span>
        </header>

        <section className="delivery-progress" aria-label="Delivery progress">
          <div className="delivery-progress__intro">
            <span>
              <FiTruck />
            </span>
            <div>
              <small>Delivery status</small>
              <h2>Your order has been delivered</h2>
              <p>
                Tracking number: <strong>{order.trackingNumber}</strong>
              </p>
            </div>
          </div>
          <ol>
            <li className="is-complete">
              <span>
                <FiCheck />
              </span>
              <b>Order placed</b>
            </li>
            <li className="is-complete">
              <span>
                <FiCheck />
              </span>
              <b>Dispatched</b>
            </li>
            <li className="is-complete">
              <span>
                <FiCheck />
              </span>
              <b>Delivered</b>
            </li>
          </ol>
        </section>

        <section
          className="order-store-binding"
          aria-label="Bound seller store"
        >
          <div className="order-store-binding__icon">
            <FiLink />
          </div>
          <div>
            <small>Seller store</small>
            <h2>{seller?.name || "Store not assigned"}</h2>
            {seller && <p>This order is linked to this ERP seller record.</p>}
          </div>
          {seller?.storeUrl ? (
            <a
              className="btn btn--outline"
              href={seller.storeUrl}
              target="_blank"
              rel="noreferrer"
              aria-label={`Visit ${seller.name} store`}
            >
              {seller.storeLabel || seller.storeUrl} <FiExternalLink />
            </a>
          ) : seller ? (
            <span className="status-badge status-badge--pending">
              <FiLink /> Store link pending
            </span>
          ) : null}
          {seller && (
            <Link to={`/customers/${seller.id}`}>
              View seller record <FiArrowRight />
            </Link>
          )}
        </section>

        <div className="order-detail-grid">
          <section className="account-card order-items">
            <div className="account-card__heading">
              <span>
                <FiPackage />
              </span>
              <h2>Order summary</h2>
            </div>
            <article>
              <img src={order.image} alt={order.product} />
              <div>
                <p className="eyebrow">Footwear</p>
                <h3>{order.product}</h3>
                <p>Quantity: {order.quantityLabel}</p>
                <p>Unit price: {formatOrderAmount(order.unitPrice)} per pair</p>
              </div>
              <strong>{formatOrderAmount(order.amount)}</strong>
            </article>
            <dl className="order-totals">
              <div>
                <dt>Items</dt>
                <dd>{order.quantity}</dd>
              </div>
              <div>
                <dt>Unit price</dt>
                <dd>{formatOrderAmount(order.unitPrice)}</dd>
              </div>
              <div>
                <dt>Total</dt>
                <dd>{formatOrderAmount(order.amount)}</dd>
              </div>
            </dl>
          </section>

          <aside className="account-card delivery-address">
            <div className="account-card__heading">
              <span>
                <FiMapPin />
              </span>
              <h2>Delivery address</h2>
            </div>
            <address>
              <strong>{order.recipient}</strong>
              <span>{order.address}</span>
            </address>
            <dl>
              <div>
                <dt>Tracking</dt>
                <dd>{order.trackingNumber}</dd>
              </div>
              <div>
                <dt>Courier status</dt>
                <dd>{order.carrierStatus}</dd>
              </div>
            </dl>
          </aside>
        </div>
      </div>
    </main>
  );
}

export default OrderDetail;
